/**
 * CDEK Delivery Picker for Telegram Mini App
 * Ported from website-repo/src/features/checkout/ui/cdek-delivery-picker.tsx
 *
 * Mobile-only layout (no desktop sidebar).
 * Controlled by useDeliveryStore(widgetOpen).
 * Telegram BackButton + haptic feedback.
 */

import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import {
  AlertCircle,
  Building2,
  Check,
  Clock,
  CreditCard,
  List,
  MapPin,
  Minus,
  Navigation,
  Package,
  Plus,
  RefreshCw,
  Search,
  Truck,
  X,
} from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { cn } from '@/shared/lib/utils';
import { config } from '@/shared/lib/config';
import { useTelegram } from '@/shared/lib/telegram';
import { getVkLocationCoords } from '@/shared/lib/telegram/vk-bridge-adapter';
import { useCart } from '@/features/cart/api/use-cart';
import { useCartFingerprint } from '../lib/use-cart-fingerprint';
import { useDeliveryStore } from '../model/use-delivery-store';
import type { CdekDeliveryInfo, DoorAddress } from '../model/use-delivery-store';
import { DoorAddressForm } from './door-address-form';
import {
  usePickupPointsByBounds,
  useDeliveryTariffs,
  snapBounds,
  POINTS_STALE,
} from '../hooks/use-cdek-queries';
import {
  type PickupPoint,
  type PickupPointBounds,
  type CdekCity,
  type CartItemForApi,
  type TariffOption,
  type TariffsGroup,
  fetchPickupPointsByBounds,
  fetchCitiesByName,
  pickTariffForPoint,
  formatDays,
  formatPrice,
} from '../api/cdek.api';

// ============================================================================
// Yandex Maps loader
// ============================================================================

let yandexMapsPromise: Promise<void> | null = null;

function loadYandexMaps(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve();
  if ((window as any).ymaps) return Promise.resolve();
  if (yandexMapsPromise) return yandexMapsPromise;

  const apiKey = config.yandexMapsApiKey;
  if (!apiKey) return Promise.reject(new Error('Yandex Maps API key not configured'));

  yandexMapsPromise = new Promise((resolve, reject) => {
    const existing = document.getElementById('yandex-maps-api') as HTMLScriptElement | null;
    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('Yandex Maps script failed')), { once: true });
      return;
    }
    const script = document.createElement('script');
    script.id = 'yandex-maps-api';
    script.src = `https://api-maps.yandex.ru/2.1/?apikey=${encodeURIComponent(apiKey)}&lang=ru_RU`;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => { yandexMapsPromise = null; reject(new Error('Yandex Maps script failed')); };
    document.head.appendChild(script);
  });
  return yandexMapsPromise;
}

// ============================================================================
// Map marker SVG + cache
// ============================================================================

const DEFAULT_CENTER: [number, number] = [55.7558, 37.6176];
const DEFAULT_ZOOM = 10;
const MIN_POINTS_ZOOM = 9;
const MAP_ACCENT = '#C73B0D';
const MAP_ACCENT_DARK = '#9E2F0A';
const VIEWPORT_DEBOUNCE = 500;

function buildMapMarkerDataUri(kind: 'PVZ' | 'POSTAMAT' | 'DOOR', selected = false) {
  const shadow = `<filter id="s" x="-25%" y="-15%" width="150%" height="150%"><feDropShadow dx="0" dy="3" stdDeviation="2.5" flood-color="#000" flood-opacity=".2"/></filter>`;
  const teardrop = `M18 2C9.16 2 2 8.96 2 17.55c0 11.42 16 29.45 16 29.45s16-18.03 16-29.45C34 8.96 26.84 2 18 2Z`;

  if (kind === 'PVZ') {
    const fill = selected ? MAP_ACCENT_DARK : MAP_ACCENT;
    const icon = `<polygon points="18,9 11,16 25,16" fill="rgba(255,255,255,.95)"/><rect x="11" y="16" width="14" height="8" fill="rgba(255,255,255,.95)"/><rect x="15.5" y="19.5" width="5" height="4.5" fill="${fill}"/>`;
    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="36" height="44" viewBox="0 0 36 44">${shadow}<path filter="url(#s)" d="${teardrop}" fill="${fill}"/>${icon}</svg>`)}`;
  }
  if (kind === 'POSTAMAT') {
    const fill = selected ? '#0C0A09' : '#1C1917';
    const shape = `M7 2 L29 2 Q34 2 34 7 L34 27 Q34 32 29 32 L23 32 L18 44 L13 32 L7 32 Q2 32 2 27 L2 7 Q2 2 7 2 Z`;
    const icon = `<rect x="11" y="9" width="14" height="14" rx="1.5" fill="rgba(255,255,255,.95)"/><line x1="11" y1="14" x2="25" y2="14" stroke="${fill}" stroke-width="1.5"/><rect x="15" y="19" width="6" height="4" rx="1" fill="${fill}"/>`;
    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="36" height="44" viewBox="0 0 36 44">${shadow}<path filter="url(#s)" d="${shape}" fill="${fill}"/>${icon}</svg>`)}`;
  }
  const fill = selected ? MAP_ACCENT_DARK : MAP_ACCENT;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="36" height="44" viewBox="0 0 36 44">${shadow}<path filter="url(#s)" d="${teardrop}" fill="${fill}"/><circle cx="18" cy="17.5" r="7" fill="rgba(255,255,255,.95)"/></svg>`)}`;
}

const MARKER = {
  PVZ:          buildMapMarkerDataUri('PVZ',      false),
  PVZ_SEL:      buildMapMarkerDataUri('PVZ',      true),
  POSTAMAT:     buildMapMarkerDataUri('POSTAMAT', false),
  POSTAMAT_SEL: buildMapMarkerDataUri('POSTAMAT', true),
  DOOR:         buildMapMarkerDataUri('DOOR',     false),
  DOOR_SEL:     buildMapMarkerDataUri('DOOR',     true),
} as const;

function markerHref(type: 'PVZ' | 'POSTAMAT' | 'DOOR', selected: boolean) {
  if (type === 'POSTAMAT') return selected ? MARKER.POSTAMAT_SEL : MARKER.POSTAMAT;
  if (type === 'DOOR')     return selected ? MARKER.DOOR_SEL     : MARKER.DOOR;
  return selected ? MARKER.PVZ_SEL : MARKER.PVZ;
}

function toGeoFeature(p: PickupPoint) {
  return {
    type: 'Feature' as const,
    id: p.code,
    geometry: { type: 'Point' as const, coordinates: [p.latitude, p.longitude] },
    properties: { hintContent: p.address, balloonContent: p.fullAddress ?? p.address },
    options: {
      iconLayout: 'default#image',
      iconImageHref: markerHref(p.type, false),
      iconImageSize: [36, 44],
      iconImageOffset: [-18, -44],
    },
  };
}

// ============================================================================
// Geocode cache
// ============================================================================

const _geocodeCache = new Map<string, any>();

function cacheKey(latOrQuery: number | string, lon?: number): string {
  if (typeof latOrQuery === 'string') return latOrQuery.toLowerCase().trim();
  return `${latOrQuery.toFixed(2)},${(lon ?? 0).toFixed(2)}`;
}

async function cachedGeocode(queryOrCoords: string | [number, number], options?: object): Promise<any> {
  const key = Array.isArray(queryOrCoords)
    ? cacheKey(queryOrCoords[0], queryOrCoords[1])
    : cacheKey(queryOrCoords);
  if (_geocodeCache.has(key)) return _geocodeCache.get(key);
  const ymaps = (window as any).ymaps;
  const result = await Promise.resolve(ymaps.geocode(queryOrCoords, options));
  _geocodeCache.set(key, result);
  return result;
}

// ============================================================================
// Geolocation
// ============================================================================

async function getUserLocationCoords(): Promise<[number, number] | null> {
  if (typeof window === 'undefined' || !('geolocation' in navigator)) return null;
  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = Number(pos.coords.latitude);
        const lon = Number(pos.coords.longitude);
        resolve(Number.isFinite(lat) && Number.isFinite(lon) ? [lat, lon] : null);
      },
      () => resolve(null),
      { enableHighAccuracy: false, maximumAge: 60_000, timeout: 7_000 },
    );
  });
}

// ============================================================================
// Geocoding helpers
// ============================================================================

function pickGeoComponent(components: Array<{ kind?: string; name?: string }> | undefined, kind: string) {
  return components?.find((c) => c.kind === kind)?.name?.trim() ?? '';
}

function normalizeCityName(value: string) {
  return value
    .toLowerCase()
    .replace(/ё/g, 'е')
    .replace(/^[\s.,-]*(?:г\.|город)\s+/i, '')
    .replace(/[.,()"]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function uniqueCityCandidates(values: string[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const value of values) {
    const normalized = normalizeCityName(value);
    if (!normalized || seen.has(normalized)) continue;
    seen.add(normalized);
    result.push(value.trim());
  }
  return result;
}

function isFederalCity(value: string) {
  const n = normalizeCityName(value);
  return n === 'москва' || n === 'санкт-петербург' || n === 'севастополь';
}

function extractPostalCodeFromMeta(meta: any): string | null {
  const direct = meta?.Address?.postal_code || meta?.Address?.postalCode;
  if (direct) return String(direct).replace(/\D/g, '') || null;
  const formatted = `${meta?.Address?.formatted ?? ''} ${meta?.text ?? ''}`;
  const match = formatted.match(/\b\d{6}\b/);
  return match?.[0] ?? null;
}

interface GeoLocationContext {
  cityName: string;
  cityCandidates: string[];
  regionName: string | null;
  postalCode: string | null;
}

function parseLocationContextFromGeoObject(geoObject: any): GeoLocationContext {
  const meta = geoObject.properties.get('metaDataProperty.GeocoderMetaData') ?? {};
  const components = meta.Address?.Components as Array<{ kind?: string; name?: string }> | undefined;
  const locality = pickGeoComponent(components, 'locality');
  const province = pickGeoComponent(components, 'province');
  const area = pickGeoComponent(components, 'area');
  const district = pickGeoComponent(components, 'district');
  const objectName = String(geoObject.properties.get('name') || '');
  const candidates = uniqueCityCandidates([
    locality,
    isFederalCity(province) ? province : '',
    area, district, objectName,
  ].filter(Boolean));
  return {
    cityName: candidates[0] ?? '',
    cityCandidates: candidates,
    regionName: isFederalCity(province) ? null : province || null,
    postalCode: extractPostalCodeFromMeta(meta),
  };
}

async function resolveCdekCityFromLocationContext(context: GeoLocationContext): Promise<CdekCity | null> {
  const candidates = context.cityCandidates.length > 0 ? context.cityCandidates : context.cityName ? [context.cityName] : [];
  if (context.postalCode) {
    const postalCities = await fetchCitiesByName(candidates[0] ?? '', 8, undefined, context.postalCode, context.regionName);
    const normalizedCandidates = candidates.map(normalizeCityName).filter(Boolean);
    const exact = postalCities.find((c) => normalizedCandidates.includes(normalizeCityName(c.city)));
    if (exact) return exact;
    if (postalCities[0]) return postalCities[0];
  }
  for (const candidate of candidates) {
    const normalized = normalizeCityName(candidate);
    if (!normalized) continue;
    const cities = await fetchCitiesByName(candidate, 8, undefined, null, context.regionName);
    const exact = cities.find((c) => normalizeCityName(c.city) === normalized);
    if (exact) return exact;
    if (cities[0]) return cities[0];
  }
  return null;
}

interface DoorMapSelection {
  latitude: number;
  longitude: number;
  title: string;
  subtitle: string;
}

interface DoorSearchResult extends DoorMapSelection {
  id: string;
  address: DoorAddress;
  cityName?: string;
  cityCandidates?: string[];
  regionName?: string | null;
  postalCode?: string | null;
}

function parseDoorAddressFromGeoObject(geoObject: any): { address: DoorAddress; title: string; subtitle: string } {
  const meta = geoObject.properties.get('metaDataProperty.GeocoderMetaData') ?? {};
  const components = meta.Address?.Components as Array<{ kind?: string; name?: string }> | undefined;
  const street = pickGeoComponent(components, 'street') || pickGeoComponent(components, 'district');
  const house = pickGeoComponent(components, 'house');
  const locality = pickGeoComponent(components, 'locality') || pickGeoComponent(components, 'province');
  const name = geoObject.properties.get('name') || meta.Address?.formatted || meta.text || '';
  const description = geoObject.properties.get('description') || locality || '';
  return {
    address: {
      street: street || String(name).replace(new RegExp(`\\s*${house}$`), '').trim(),
      house,
    },
    title: String(name || 'Выбранная точка').replace(/^Россия,\s*/i, ''),
    subtitle: String(description || '').replace(/^Россия,\s*/i, ''),
  };
}

async function resolveDoorSelectionFromCoords(coords: [number, number]): Promise<{ selection: DoorMapSelection; address: DoorAddress; locationContext?: GeoLocationContext }> {
  const ymaps = (window as any).ymaps;
  if (!ymaps) return { selection: { latitude: coords[0], longitude: coords[1], title: 'Выбранная точка', subtitle: '' }, address: { street: '', house: '' } };
  const result = await cachedGeocode(coords, { results: 1 });
  const geoObject = result.geoObjects.get(0);
  if (!geoObject) return { selection: { latitude: coords[0], longitude: coords[1], title: 'Выбранная точка', subtitle: '' }, address: { street: '', house: '' } };
  const parsed = parseDoorAddressFromGeoObject(geoObject);
  return {
    selection: { latitude: coords[0], longitude: coords[1], title: parsed.title, subtitle: parsed.subtitle },
    address: parsed.address,
    locationContext: parseLocationContextFromGeoObject(geoObject),
  };
}

type MapBounds = [[number, number], [number, number]];
interface MapFocus { coords: [number, number]; zoom: number; key: number; }

function toPickupPointBounds(bounds: MapBounds): PickupPointBounds {
  const [[southLike, westLike], [northLike, eastLike]] = bounds;
  return {
    south: Math.min(southLike, northLike),
    west: westLike,
    north: Math.max(southLike, northLike),
    east: eastLike,
  };
}

function buildDoorAddressText(city: CdekCity | null, address: DoorAddress) {
  return [city?.city, address.street?.trim(), address.house?.trim() ? `д. ${address.house.trim()}` : null, address.building?.trim() ? `корп. ${address.building.trim()}` : null, address.apartment?.trim() ? `кв. ${address.apartment.trim()}` : null].filter(Boolean).join(', ');
}

function getShortWorkTime(workTime: string) { return workTime.split(',')[0]?.trim() ?? workTime.trim(); }
function getPointLabel(point: PickupPoint) { return point.type === 'POSTAMAT' ? 'Постамат' : 'Пункт выдачи'; }

function useDebouncedValue<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

type PickerTab = 'pickup' | 'door';

// ============================================================================
// MapControls
// ============================================================================

function MapControls({ mapRef, onLocate }: { mapRef: React.RefObject<any>; onLocate?: () => void }) {
  const zoom = (delta: number) => { const map = mapRef.current; if (!map) return; void map.setZoom(map.getZoom() + delta, { duration: 200 }); };
  return (
    <div className="absolute top-1/2 right-3 z-10 flex -translate-y-1/2 flex-col gap-1.5">
      {onLocate && (
        <button type="button" aria-label="Моё местоположение" onClick={onLocate}
          className="grid h-10 w-10 place-items-center rounded-xl bg-paper dark:bg-dark-card text-ink dark:text-white shadow-md shadow-black/10 transition-transform active:scale-95">
          <Navigation className="h-4 w-4" />
        </button>
      )}
      <div className="flex flex-col overflow-hidden rounded-xl shadow-md shadow-black/10">
        <button type="button" aria-label="Приблизить" onClick={() => zoom(1)}
          className="grid h-10 w-10 place-items-center bg-paper dark:bg-dark-card text-ink dark:text-white transition-colors active:bg-muted/50">
          <Plus className="h-4 w-4" />
        </button>
        <div className="h-px bg-silk/50 dark:bg-dark-border" />
        <button type="button" aria-label="Отдалить" onClick={() => zoom(-1)}
          className="grid h-10 w-10 place-items-center bg-paper dark:bg-dark-card text-ink dark:text-white transition-colors active:bg-muted/50">
          <Minus className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// AddressSearchBar
// ============================================================================

function AddressSearchBar({ city, placeholder, onSelect }: { city: CdekCity | null; placeholder: string; onSelect: (value: DoorSearchResult) => void }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<DoorSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const skipNextSearchRef = useRef(false);
  const debouncedQuery = useDebouncedValue(query, 500);

  useEffect(() => {
    const q = debouncedQuery.trim();
    const ymaps = (window as any).ymaps;
    if (!q || q.length < 3 || !ymaps) { setResults([]); setSearching(false); return; }
    if (skipNextSearchRef.current) { skipNextSearchRef.current = false; setSearching(false); setResults([]); return; }
    let cancelled = false;
    setSearching(true);
    const searchText = city?.city ? `${city.city}, ${q}` : q;
    cachedGeocode(searchText, { results: 6 })
      .then((result: any) => {
        if (cancelled) return;
        const items: DoorSearchResult[] = [];
        result.geoObjects.each((geoObject: any, index: number) => {
          const parsed = parseDoorAddressFromGeoObject(geoObject);
          const locationContext = parseLocationContextFromGeoObject(geoObject);
          const coords = geoObject.geometry.getCoordinates() as [number, number];
          items.push({ id: `${index}-${parsed.title}-${coords.join(',')}`, title: parsed.title, subtitle: parsed.subtitle, latitude: coords[0], longitude: coords[1], address: parsed.address, cityName: locationContext.cityName, cityCandidates: locationContext.cityCandidates, regionName: locationContext.regionName, postalCode: locationContext.postalCode });
        });
        setResults(items);
      })
      .catch(() => { if (!cancelled) setResults([]); })
      .finally(() => { if (!cancelled) setSearching(false); });
    return () => { cancelled = true; };
  }, [city?.city, debouncedQuery]);

  const handleSelect = (item: DoorSearchResult) => {
    skipNextSearchRef.current = true;
    setQuery(item.title);
    setResults([]);
    onSelect(item);
  };

  return (
    <div className="absolute left-4 top-4 z-10 w-[calc(100%-2rem)]">
      <label className="flex h-12 items-center gap-3 rounded-xl border border-silk/50 dark:border-dark-border bg-paper/95 dark:bg-dark-card/95 px-4 text-[14px] shadow-lg shadow-black/10 backdrop-blur">
        <Search className="h-5 w-5 shrink-0 text-muted-foreground" />
        <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={placeholder}
          className="h-full min-w-0 flex-1 bg-transparent text-ink dark:text-white outline-none placeholder:text-muted-foreground" />
      </label>
      {(results.length > 0 || searching) && (
        <div className="mt-2 max-h-72 overflow-auto rounded-xl border border-silk/50 dark:border-dark-border bg-paper dark:bg-dark-card shadow-xl shadow-black/10">
          {searching && <div className="px-4 py-3 text-[13px] text-muted-foreground">Ищем адрес...</div>}
          {results.map((item) => (
            <button key={item.id} type="button" onClick={() => handleSelect(item)}
              className="block w-full px-4 py-3 text-left transition-colors active:bg-muted/30">
              <span className="block text-[14px] font-semibold text-ink dark:text-white">{item.title}</span>
              {item.subtitle && <span className="mt-0.5 block text-[12px] text-muted-foreground">{item.subtitle}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// PickupPointList
// ============================================================================

function PickupPointList({ points, totalCount, canLoadArea, selectedCode, loading, tariff, allTariffs, onSelect }: {
  points: PickupPoint[]; totalCount: number; canLoadArea: boolean;
  selectedCode: string | null; loading: boolean; tariff: TariffOption | null;
  allTariffs: TariffsGroup | null; onSelect: (code: string) => void;
}) {
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!selectedCode || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-code="${selectedCode}"]`);
    el?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [selectedCode]);

  if (loading) {
    return (
      <div className="min-h-0 flex-1 overflow-auto p-4">
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="animate-pulse rounded-2xl border border-silk/30 dark:border-dark-border p-4">
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 shrink-0 rounded-xl bg-muted/50" />
                <div className="flex-1 space-y-2 pt-0.5">
                  <div className="h-3 w-14 rounded-full bg-muted/50" />
                  <div className="h-4 w-3/4 rounded-full bg-muted/50" />
                  <div className="h-3 w-1/2 rounded-full bg-muted/50" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (points.length === 0) {
    const title = !canLoadArea ? 'Найдите адрес или приблизьте карту' : totalCount > 0 ? 'В этой области карты точек нет' : 'ПВЗ и постаматы не найдены';
    const subtitle = !canLoadArea ? 'Точки CDEK загрузятся, когда карта будет ближе к нужному району' : totalCount > 0 ? 'Передвиньте карту или уменьшите масштаб' : 'Попробуйте другой адрес';
    return (
      <div className="flex min-h-0 flex-1 items-center justify-center p-6 text-center">
        <div>
          <div className="mx-auto mb-4 grid h-12 w-12 place-items-center rounded-2xl bg-muted/50">
            <MapPin className="h-5 w-5 text-muted-foreground" />
          </div>
          <p className="text-[14px] font-semibold text-ink dark:text-white">{title}</p>
          <p className="mt-1 text-[12px] text-muted-foreground">{subtitle}</p>
        </div>
      </div>
    );
  }

  const pvzTariff = allTariffs ? pickTariffForPoint('PVZ', allTariffs) : null;

  return (
    <div ref={listRef} className="min-h-0 flex-1 overflow-auto">
      <div className="flex items-center justify-between px-4 pb-2 pt-3">
        <span className="text-[12px] font-medium text-muted-foreground">
          {points.length} на карте{totalCount !== points.length ? ` из ${totalCount}` : ''}
        </span>
        {pvzTariff && (
          <span className="text-[12px] font-semibold text-ink dark:text-white">
            {formatDays(pvzTariff.periodMin, pvzTariff.periodMax)} · {formatPrice(pvzTariff.deliveryCost)}
          </span>
        )}
      </div>
      <div className="space-y-2 px-4 pb-4">
        {points.map((point) => {
          const isSelected = point.code === selectedCode;
          const pointTariff = isSelected ? (tariff ?? pickTariffForPoint(point.type, allTariffs)) : null;
          return (
            <button key={point.code} data-code={point.code} type="button" onClick={() => onSelect(point.code)}
              className={cn('w-full rounded-2xl border p-4 text-left transition-all duration-150',
                isSelected ? 'border-primary/25 bg-primary/5 dark:bg-primary/10 shadow-sm' : 'border-silk/50 dark:border-dark-border bg-paper dark:bg-dark-card active:bg-muted/30')}>
              <div className="flex items-start gap-3">
                <div className={cn('grid h-10 w-10 shrink-0 place-items-center rounded-xl',
                  point.type === 'POSTAMAT' ? 'bg-primary/10 text-primary' : 'bg-muted/50 text-muted-foreground')}>
                  {point.type === 'POSTAMAT' ? <Package className="h-4 w-4" /> : <Building2 className="h-4 w-4" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className={cn('rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide',
                      point.type === 'POSTAMAT' ? 'bg-primary/10 text-primary' : 'bg-muted/50 text-muted-foreground')}>
                      {point.type === 'POSTAMAT' ? 'Постамат' : 'ПВЗ'}
                    </span>
                    {isSelected && <span className="flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-bold text-primary"><Check className="h-3 w-3" />Выбран</span>}
                    {point.hasCard && !isSelected && <CreditCard className="h-3.5 w-3.5 text-muted-foreground/40" />}
                  </div>
                  <p className="mt-1.5 text-[14px] font-semibold leading-snug text-ink dark:text-white">{point.address}</p>
                  {point.workTime && (
                    <p className="mt-1 flex items-center gap-1.5 text-[12px] text-muted-foreground">
                      <Clock className="h-3.5 w-3.5 shrink-0" />{getShortWorkTime(point.workTime)}
                    </p>
                  )}
                  {isSelected && pointTariff && (
                    <p className="mt-1.5 text-[12px] font-semibold text-ink dark:text-white">
                      {formatDays(pointTariff.periodMin, pointTariff.periodMax)} · {formatPrice(pointTariff.deliveryCost)}
                    </p>
                  )}
                  {isSelected && point.addressComment && (
                    <p className="mt-1 flex items-center gap-1.5 text-[12px] text-muted-foreground">
                      <MapPin className="h-3.5 w-3.5 shrink-0 text-muted-foreground/60" />{point.addressComment}
                    </p>
                  )}
                </div>
                {isSelected && (
                  <div className="ml-1 grid h-6 w-6 shrink-0 place-items-center rounded-full bg-primary text-white">
                    <Check className="h-3.5 w-3.5" />
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================================
// DoorPanel — wraps miniapp's existing DoorAddressForm
// ============================================================================

function DoorPanel({ city, address, selection, onAddressChange, tariff, loading }: {
  city: CdekCity | null; address: DoorAddress; selection: DoorMapSelection | null;
  onAddressChange: (value: DoorAddress) => void; tariff: TariffOption | null; loading: boolean;
}) {
  return (
    <div className="min-h-0 flex-1 overflow-auto p-4">
      <div className="mb-4 rounded-2xl border border-silk/50 dark:border-dark-border bg-paper dark:bg-dark-card p-4 shadow-sm">
        <div className="mb-3 flex items-center gap-2.5">
          <div className="grid h-9 w-9 place-items-center rounded-xl bg-muted/50">
            <Truck className="h-4 w-4 text-muted-foreground" />
          </div>
          <p className="text-[12px] font-semibold uppercase tracking-wider text-muted-foreground">Адрес доставки</p>
        </div>
        {selection ? (
          <>
            <p className="text-[15px] font-bold leading-snug text-ink dark:text-white">{selection.title}</p>
            <p className="mt-0.5 text-[12px] leading-relaxed text-muted-foreground">{selection.subtitle || city?.city || 'Город определится по адресу'}</p>
          </>
        ) : (
          <>
            <p className="text-[14px] font-semibold text-ink dark:text-white">{city?.city ?? 'Город доставки не выбран'}</p>
            <p className="mt-1 text-[12px] leading-relaxed text-muted-foreground">Нажмите на карту или введите адрес в строку поиска</p>
          </>
        )}
        {(loading || tariff) && (
          <div className="mt-3 border-t border-silk/30 dark:border-dark-border pt-3">
            <p className="text-[12px] font-medium text-muted-foreground">
              {loading ? 'Рассчитываем тариф...' : tariff ? `${formatDays(tariff.periodMin, tariff.periodMax)} · ${formatPrice(tariff.deliveryCost)}` : ''}
            </p>
          </div>
        )}
      </div>
      <DoorAddressForm value={address} onChange={onAddressChange} />
    </div>
  );
}

// ============================================================================
// YandexPickupMap
// ============================================================================

function YandexPickupMap({ city, focus, points, selectedCode, onSelect, onMapClick, onViewportChange, onReadyChange, onLocate, initialCenter, initialZoom }: {
  city: CdekCity | null; focus: MapFocus | null; points: PickupPoint[]; selectedCode: string | null;
  onSelect: (code: string) => void; onMapClick: (coords: [number, number]) => void;
  onViewportChange: (bounds: MapBounds, center: [number, number], zoom: number) => void;
  onReadyChange: (ready: boolean) => void; onLocate?: () => void;
  initialCenter?: [number, number]; initialZoom?: number;
}) {
  const initCenterRef = useRef(initialCenter ?? DEFAULT_CENTER);
  const initZoomRef = useRef(initialZoom ?? DEFAULT_ZOOM);
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);
  const objectManagerRef = useRef<any>(null);
  const renderedIdsRef = useRef<Set<string>>(new Set());
  const prevSelectedRef = useRef<string | null>(null);
  const pointsRef = useRef(points);
  pointsRef.current = points;
  const lastSelectFromMapRef = useRef(false);
  const onSelectRef = useRef(onSelect);
  const onMapClickRef = useRef(onMapClick);
  const onViewportChangeRef = useRef(onViewportChange);
  const lastObjectClickAtRef = useRef(0);
  const viewportTimerRef = useRef<number | null>(null);
  onSelectRef.current = onSelect;
  onMapClickRef.current = onMapClick;
  onViewportChangeRef.current = onViewportChange;

  useEffect(() => {
    let cancelled = false;
    onReadyChange(false);
    loadYandexMaps()
      .then(() => {
        const ymaps = (window as any).ymaps;
        if (cancelled || !containerRef.current || !ymaps) return;
        ymaps.ready(() => {
          if (cancelled || !containerRef.current || mapRef.current) return;
          const map = new ymaps.Map(containerRef.current, { center: initCenterRef.current, zoom: initZoomRef.current, controls: [] }, { suppressMapOpenBlock: true, yandexMapAutoSwitch: false, yandexMapDisablePoiInteractivity: true, minZoom: 5, maxZoom: 19 });
          map.behaviors.enable('scrollZoom');
          const om = new ymaps.ObjectManager({ clusterize: true, gridSize: 48, clusterDisableClickZoom: false, geoObjectOpenBalloonOnClick: false });
          om.clusters.options.set({ preset: 'islands#redClusterIcons' });
          om.objects.events.add('click', (e: any) => {
            e.stopPropagation?.();
            lastObjectClickAtRef.current = Date.now();
            lastSelectFromMapRef.current = true;
            onSelectRef.current(String(e.get('objectId')));
          });
          map.events.add('click', (e: any) => {
            if (Date.now() - lastObjectClickAtRef.current < 500) return;
            onMapClickRef.current(e.get('coords') as [number, number]);
          });
          const emitViewport = () => {
            const bounds = map.getBounds() as MapBounds | null;
            const center = map.getCenter() as [number, number] | null;
            const zoom = Number(map.getZoom());
            if (bounds && center && Number.isFinite(zoom)) onViewportChangeRef.current(bounds, center, zoom);
          };
          map.events.add('boundschange', () => {
            if (viewportTimerRef.current) window.clearTimeout(viewportTimerRef.current);
            viewportTimerRef.current = window.setTimeout(emitViewport, VIEWPORT_DEBOUNCE);
          });
          map.geoObjects.add(om);
          mapRef.current = map;
          objectManagerRef.current = om;
          onReadyChange(true);
          window.setTimeout(emitViewport, 0);
        });
      })
      .catch(() => onReadyChange(true));
    return () => {
      cancelled = true;
      onReadyChange(false);
      if (viewportTimerRef.current) window.clearTimeout(viewportTimerRef.current);
      mapRef.current?.destroy();
      mapRef.current = null;
      objectManagerRef.current = null;
      renderedIdsRef.current = new Set();
      prevSelectedRef.current = null;
    };
  }, [onReadyChange]);

  // Effect A: diff-based add/remove deferred to rAF
  useEffect(() => {
    const om = objectManagerRef.current;
    if (!om) return;
    const newIds = new Set(points.map((p) => p.code));
    const prev = renderedIdsRef.current;
    if (newIds.size === prev.size) {
      let identical = true;
      for (const id of newIds) { if (!prev.has(id)) { identical = false; break; } }
      if (identical) return;
    }
    const toAdd    = points.filter((p) => !prev.has(p.code));
    const toRemove = [...prev].filter((id) => !newIds.has(id));
    const rafId = requestAnimationFrame(() => {
      if (toAdd.length) om.add({ type: 'FeatureCollection', features: toAdd.map(toGeoFeature) });
      if (toRemove.length) om.remove(toRemove);
      renderedIdsRef.current = newIds;
    });
    return () => { cancelAnimationFrame(rafId); };
  }, [points]);

  // Effect A2: geocode city when no points visible
  useEffect(() => {
    const map = mapRef.current;
    const ymaps = (window as any).ymaps;
    if (!map || !city || focus || !ymaps) return;
    if (renderedIdsRef.current.size > 0) return;
    let cancelled = false;
    void cachedGeocode(city.city, { results: 1 })
      .then((r: any) => {
        if (cancelled || !mapRef.current) return;
        const coords = r.geoObjects.get(0)?.geometry?.getCoordinates();
        if (coords) mapRef.current.setCenter(coords, 11);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [city, focus]);

  // Effect B: targeted icon update — only on selectedCode change
  useEffect(() => {
    const om = objectManagerRef.current;
    const map = mapRef.current;
    if (!om) return;
    const prev = prevSelectedRef.current;
    const curr = selectedCode;
    if (prev && prev !== curr) {
      const prevPoint = pointsRef.current.find((p) => p.code === prev);
      if (prevPoint) om.objects.setObjectOptions(prev, { iconLayout: 'default#image', iconImageHref: markerHref(prevPoint.type, false), iconImageSize: [36, 44], iconImageOffset: [-18, -44] });
    }
    if (curr) {
      const currPoint = pointsRef.current.find((p) => p.code === curr);
      if (currPoint) {
        om.objects.setObjectOptions(curr, { iconLayout: 'default#image', iconImageHref: markerHref(currPoint.type, true), iconImageSize: [36, 44], iconImageOffset: [-18, -44] });
        const fromMap = lastSelectFromMapRef.current;
        lastSelectFromMapRef.current = false;
        if (map && prev !== curr && !fromMap) map.panTo([currPoint.latitude, currPoint.longitude], { duration: 250, flying: false }).catch(() => undefined);
      }
    }
    prevSelectedRef.current = curr;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCode]);

  // Focus effect
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !focus) return;
    map.setCenter(focus.coords, focus.zoom, { duration: 250 }).catch(() => undefined);
  }, [focus]);

  return (
    <div className="relative h-full w-full">
      <div ref={containerRef} className="h-full w-full" />
      <MapControls mapRef={mapRef} onLocate={onLocate} />
    </div>
  );
}

// ============================================================================
// YandexDoorMap
// ============================================================================

function YandexDoorMap({ city, address, selection, initialCenter, initialZoom, onAddressChange, onSelectionChange, onCityChange, onReadyChange, getLocationCoords }: {
  city: CdekCity | null; address: DoorAddress; selection: DoorMapSelection | null;
  initialCenter?: [number, number]; initialZoom?: number;
  onAddressChange: (value: DoorAddress) => void;
  onSelectionChange: (value: DoorMapSelection | null) => void;
  onCityChange: (city: CdekCity, source: 'map' | 'search') => void;
  onReadyChange: (ready: boolean) => void;
  getLocationCoords?: () => Promise<[number, number] | null>;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);
  const placemarkRef = useRef<any>(null);
  const addressRef = useRef(address);
  const selectionRef = useRef(selection);
  const onAddressChangeRef = useRef(onAddressChange);
  const onSelectionChangeRef = useRef(onSelectionChange);
  const onCityChangeRef = useRef(onCityChange);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<DoorSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [reverseGeocoding, setReverseGeocoding] = useState(false);
  const debouncedSearchQuery = useDebouncedValue(searchQuery, 500);
  addressRef.current = address;
  selectionRef.current = selection;
  onAddressChangeRef.current = onAddressChange;
  onSelectionChangeRef.current = onSelectionChange;
  onCityChangeRef.current = onCityChange;

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const setMarker = useCallback((coords: [number, number], title: string, _subtitle: string) => {
    const map = mapRef.current;
    const ymaps = (window as any).ymaps;
    if (!map || !ymaps) return;
    if (!placemarkRef.current) {
      const pm = new ymaps.Placemark(coords, { hintContent: title, balloonContent: title }, { iconLayout: 'default#image', iconImageHref: markerHref('DOOR', true), iconImageSize: [36, 44], iconImageOffset: [-18, -44] });
      map.geoObjects.add(pm);
      placemarkRef.current = pm;
    } else {
      placemarkRef.current.geometry.setCoordinates(coords);
      placemarkRef.current.properties.set({ hintContent: title, balloonContent: title });
    }
    map.panTo(coords, { duration: 250, flying: false }).then(() => map.setZoom(Math.max(map.getZoom(), 16), { duration: 120 })).catch(() => undefined);
  }, []);

  const applyCityFromLocation = useCallback(async (context: GeoLocationContext, source: 'map' | 'search') => {
    const resolved = await resolveCdekCityFromLocationContext(context);
    if (resolved) onCityChangeRef.current(resolved, source);
  }, []);

  const applyDoorSelection = useCallback((sel: DoorMapSelection, addr: DoorAddress) => {
    setMarker([sel.latitude, sel.longitude], sel.title, sel.subtitle);
    onSelectionChangeRef.current(sel);
    onAddressChangeRef.current({ ...addressRef.current, street: addr.street ?? '', house: addr.house ?? '' });
  }, [setMarker]);

  const reverseGeocode = useCallback((coords: [number, number]) => {
    const ymaps = (window as any).ymaps;
    if (!ymaps) return;
    setReverseGeocoding(true);
    void resolveDoorSelectionFromCoords(coords)
      .then(async ({ selection: sel, address: addr, locationContext }) => {
        applyDoorSelection(sel, addr);
        if (locationContext) applyCityFromLocation(locationContext, 'map');
      })
      .catch(() => { /* geocoding failure is non-fatal */ })
      .finally(() => setReverseGeocoding(false));
  }, [applyCityFromLocation, applyDoorSelection]);

  // Map init
  useEffect(() => {
    let cancelled = false;
    onReadyChange(false);
    loadYandexMaps()
      .then(() => {
        const ymaps = (window as any).ymaps;
        if (cancelled || !containerRef.current || !ymaps) return;
        ymaps.ready(() => {
          if (cancelled || !containerRef.current || mapRef.current) return;
          const initialSel = selectionRef.current;
          const center = initialSel ? [initialSel.latitude, initialSel.longitude] : (initialCenter ?? DEFAULT_CENTER);
          const zoom = initialSel ? 16 : (initialZoom ?? DEFAULT_ZOOM);
          const map = new ymaps.Map(containerRef.current, { center, zoom, controls: [] }, { suppressMapOpenBlock: true, yandexMapAutoSwitch: false, yandexMapDisablePoiInteractivity: true, minZoom: 5, maxZoom: 19 });
          map.behaviors.enable('scrollZoom');
          map.events.add('click', (e: any) => reverseGeocode(e.get('coords') as [number, number]));
          mapRef.current = map;
          if (initialSel) setMarker([initialSel.latitude, initialSel.longitude], initialSel.title, initialSel.subtitle);
          onReadyChange(true);
        });
      })
      .catch(() => onReadyChange(true));
    return () => {
      cancelled = true;
      onReadyChange(false);
      mapRef.current?.destroy();
      mapRef.current = null;
      placemarkRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onReadyChange, reverseGeocode, setMarker]);

  // Address search
  useEffect(() => {
    const q = debouncedSearchQuery.trim();
    const ymaps = (window as any).ymaps;
    if (!q || q.length < 3 || !ymaps) { setResults([]); setSearching(false); return; }
    let cancelled = false;
    setSearching(true);
    const searchText = city?.city ? `${city.city}, ${q}` : q;
    cachedGeocode(searchText, { results: 6 })
      .then((result: any) => {
        if (cancelled) return;
        const items: DoorSearchResult[] = [];
        result.geoObjects.each((geoObject: any, index: number) => {
          const parsed = parseDoorAddressFromGeoObject(geoObject);
          const locationContext = parseLocationContextFromGeoObject(geoObject);
          const coords = geoObject.geometry.getCoordinates() as [number, number];
          items.push({ id: `${index}-${parsed.title}`, title: parsed.title, subtitle: parsed.subtitle, latitude: coords[0], longitude: coords[1], address: parsed.address, cityName: locationContext.cityName, cityCandidates: locationContext.cityCandidates, regionName: locationContext.regionName, postalCode: locationContext.postalCode });
        });
        setSearchResults(items);
      })
      .catch(() => { if (!cancelled) setSearchResults([]); })
      .finally(() => { if (!cancelled) setSearching(false); });
    return () => { cancelled = true; };
  }, [city?.city, debouncedSearchQuery]);

  const skipNextRef = useRef(false);
  const handleSearchSelect = (item: DoorSearchResult) => {
    skipNextRef.current = true;
    setSearchQuery(item.title);
    setSearchResults([]);
    applyDoorSelection({ latitude: item.latitude, longitude: item.longitude, title: item.title, subtitle: item.subtitle }, item.address);
    if (item.cityCandidates && item.cityCandidates.length > 0) {
      applyCityFromLocation({ cityName: item.cityName ?? '', cityCandidates: item.cityCandidates, regionName: item.regionName ?? null, postalCode: item.postalCode ?? null }, 'search');
    }
  };

  function setResults(r: DoorSearchResult[]) { setSearchResults(r); }

  const handleLocate = useCallback(async () => {
    const coords = await (getLocationCoords ? getLocationCoords() : getUserLocationCoords());
    if (coords) reverseGeocode(coords);
  }, [getLocationCoords, reverseGeocode]);

  useEffect(() => {
    if (!selection) return;
    setMarker([selection.latitude, selection.longitude], selection.title, selection.subtitle);
  }, [selection, setMarker]);

  return (
    <>
      <div ref={containerRef} className="h-full w-full" />
      <MapControls mapRef={mapRef} onLocate={handleLocate} />
      {/* Address search bar */}
      <div className="absolute left-4 top-4 z-10 w-[calc(100%-2rem)]">
        <label className="flex h-12 items-center gap-3 rounded-xl border border-silk/50 dark:border-dark-border bg-paper/95 dark:bg-dark-card/95 px-4 text-[14px] shadow-lg shadow-black/10 backdrop-blur">
          <Search className="h-5 w-5 shrink-0 text-muted-foreground" />
          <input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Введите адрес доставки"
            className="h-full min-w-0 flex-1 bg-transparent text-ink dark:text-white outline-none placeholder:text-muted-foreground" />
          {reverseGeocoding && <span className="text-[11px] text-muted-foreground">...</span>}
        </label>
        {(searchResults.length > 0 || searching) && (
          <div className="mt-2 max-h-72 overflow-auto rounded-xl border border-silk/50 dark:border-dark-border bg-paper dark:bg-dark-card shadow-xl shadow-black/10">
            {searching && <div className="px-4 py-3 text-[13px] text-muted-foreground">Ищем адрес...</div>}
            {searchResults.map((item) => (
              <button key={item.id} type="button" onClick={() => handleSearchSelect(item)}
                className="block w-full px-4 py-3 text-left transition-colors active:bg-muted/30">
                <span className="block text-[14px] font-semibold text-ink dark:text-white">{item.title}</span>
                {item.subtitle && <span className="mt-0.5 block text-[12px] text-muted-foreground">{item.subtitle}</span>}
              </button>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

// ============================================================================
// MapSkeleton
// ============================================================================

function MapSkeleton() {
  return (
    <div className="absolute inset-0 grid place-items-center bg-muted/30 dark:bg-dark-bg">
      <div className="flex flex-col items-center gap-3 text-muted-foreground">
        <MapPin className="h-8 w-8 animate-pulse" />
        <p className="text-[13px]">Загрузка карты...</p>
      </div>
    </div>
  );
}

// ============================================================================
// CdekDeliveryPicker — main component, portal overlay
// ============================================================================

export const CdekDeliveryPicker = memo(function CdekDeliveryPicker() {
  const { hapticFeedback, BackButton, disableVerticalSwipes, enableVerticalSwipes, isVk, webApp } = useTelegram();
  const queryClient = useQueryClient();

  // Delivery store
  const isOpen     = useDeliveryStore((s) => s.widgetOpen);
  const closeWidget = useDeliveryStore((s) => s.closeWidget);
  const setCdekInfo = useDeliveryStore((s) => s.setCdekInfo);
  const storeDoorAddress = useDeliveryStore((s) => s.doorAddress);
  const setDoorAddress = useDeliveryStore((s) => s.setDoorAddress);

  // Cart
  const { data: cartData } = useCart();
  const cartItems = cartData?.data?.items ?? [];
  const { cartFingerprint } = useCartFingerprint();
  const apiItems = useMemo((): CartItemForApi[] =>
    cartItems.map((item) => ({
      productType: item.productType,
      productId: item.productId,
      quantity: item.quantity,
      weight: item.productType === 'tea' && item.weight ? item.weight : undefined,
    })),
  [cartItems]);

  // UI state
  const [tab, setTab] = useState<PickerTab>('pickup');
  const [city, setCity] = useState<CdekCity | null>(null);
  const [mapBounds, setMapBounds] = useState<MapBounds | null>(null);
  const [mapZoom, setMapZoom] = useState(DEFAULT_ZOOM);
  const [mapCenter, setMapCenter] = useState<[number, number]>(DEFAULT_CENTER);
  const [mapFocus, setMapFocus] = useState<MapFocus | null>(null);
  const [selectedPointCode, setSelectedPointCode] = useState<string | null>(null);
  const [selectedPointSnapshot, setSelectedPointSnapshot] = useState<PickupPoint | null>(null);
  const [detectingCity, setDetectingCity] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mapReady, setMapReady] = useState(false);
  const [doorMapSelection, setDoorMapSelection] = useState<DoorMapSelection | null>(null);
  const [mobileListOpen, setMobileListOpen] = useState(false);
  const [mobileDoorSheetOpen, setMobileDoorSheetOpen] = useState(false);
  const [mobilePickupSheetOpen, setMobilePickupSheetOpen] = useState(false);
  const [sheetPoint, setSheetPoint] = useState<PickupPoint | null>(null);
  const [pendingSheetPoint, setPendingSheetPoint] = useState<PickupPoint | null>(null);
  const pickupSheetOpenedAtRef = useRef(0);

  // Platform-aware geolocation: VK Bridge → Telegram LocationManager → navigator.geolocation
  const getPlatformCoords = useCallback((): Promise<[number, number] | null> => {
    if (isVk) return getVkLocationCoords();

    const wa = webApp as any;
    const lm = wa?.LocationManager;

    // Telegram LocationManager (Bot API 8.0+)
    // init() is async with no callback — must listen to locationManagerUpdated event
    if (lm && typeof lm.getLocation === 'function') {
      return new Promise<[number, number] | null>((resolve) => {
        let settled = false;
        const finish = (data: any) => {
          if (settled) return;
          settled = true;
          const lat = Number(data?.latitude);
          const lon = Number(data?.longitude);
          resolve(Number.isFinite(lat) && Number.isFinite(lon) ? [lat, lon] : null);
        };

        // Safety timeout — if Telegram never responds, fall through
        const timeout = window.setTimeout(() => { if (!settled) { settled = true; resolve(null); } }, 8000);

        const doGetLocation = () => {
          window.clearTimeout(timeout);
          lm.getLocation(finish);
        };

        if (lm.isInited) {
          doGetLocation();
        } else {
          const handler = () => {
            wa.offEvent?.('locationManagerUpdated', handler);
            doGetLocation();
          };
          wa.onEvent?.('locationManagerUpdated', handler);
          lm.init();
        }
      });
    }

    // Fallback: standard browser geolocation (works on desktop / older Telegram)
    return getUserLocationCoords();
  }, [isVk, webApp]);

  // Telegram BackButton
  useEffect(() => {
    if (!isOpen) return;
    BackButton?.show?.();
    const handler = () => closeWidget();
    BackButton?.onClick?.(handler);
    return () => { BackButton?.offClick?.(handler); BackButton?.hide?.(); };
  }, [isOpen, BackButton, closeWidget]);

  // Scroll lock + swipe disable
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    disableVerticalSwipes?.();
    return () => { document.body.style.overflow = prev; enableVerticalSwipes?.(); };
  }, [isOpen, disableVerticalSwipes, enableVerticalSwipes]);

  // React Query
  const pickupBounds = useMemo(
    () => (isOpen && tab === 'pickup' && mapBounds && mapZoom >= MIN_POINTS_ZOOM ? toPickupPointBounds(mapBounds) : null),
    [isOpen, mapBounds, mapZoom, tab],
  );

  const {
    data: points = [],
    isFetching: pointsFetching,
    isLoading: pointsInitialLoading,
    error: pointsError,
    refetch: refetchPoints,
  } = usePickupPointsByBounds(pickupBounds, isOpen && tab === 'pickup');

  const preloadCityCode = useMemo(() => {
    if (selectedPointSnapshot || points.length === 0) return null;
    const freq = new Map<number, number>();
    for (const p of points) freq.set(p.cityCode, (freq.get(p.cityCode) ?? 0) + 1);
    let best = points[0].cityCode, bestCount = 0;
    for (const [code, count] of freq) { if (count > bestCount) { best = code; bestCount = count; } }
    return best;
  }, [selectedPointSnapshot, points]);

  const tariffCityCode = tab === 'pickup'
    ? (selectedPointSnapshot?.cityCode ?? preloadCityCode ?? null)
    : (city?.code ?? null);

  const { data: tariffs, isFetching: tariffsLoading, refetch: refetchTariffs } = useDeliveryTariffs(
    isOpen ? tariffCityCode : null, cartFingerprint, apiItems,
  );

  const visiblePoints = useMemo(() => {
    const base = selectedPointSnapshot && !points.some((p) => p.code === selectedPointSnapshot.code)
      ? [selectedPointSnapshot, ...points] : points;
    const seen = new Set<string>();
    return base.filter((p) => {
      const key = `${p.type}|${p.address.toLowerCase().trim()}`;
      if (seen.has(key) && p.code !== selectedPointCode) return false;
      seen.add(key);
      return true;
    });
  }, [points, selectedPointSnapshot, selectedPointCode]);

  const selectedPoint = useMemo(
    () => points.find((p) => p.code === selectedPointCode) ?? selectedPointSnapshot,
    [points, selectedPointCode, selectedPointSnapshot],
  );

  useEffect(() => {
    if (!selectedPointCode) return;
    const latest = points.find((p) => p.code === selectedPointCode);
    if (latest) setSelectedPointSnapshot(latest);
  }, [points, selectedPointCode]);

  const selectedPickupTariff = selectedPoint ? pickTariffForPoint(selectedPoint.type, tariffs ?? null) : null;
  const doorTariff = tariffs?.door ?? null;

  const cityRef = useRef<CdekCity | null>(null);
  useEffect(() => { cityRef.current = city; }, [city]);

  // Widget open/close
  useEffect(() => {
    if (!isOpen) { setPendingSheetPoint(null); return; }
    setError(null);
    setMobilePickupSheetOpen(false);
    setPendingSheetPoint(null);
    if (!city) {
      setDetectingCity(true);
      getPlatformCoords()
        .then((coords) => { if (coords) setMapFocus({ coords, zoom: 12, key: Date.now() }); })
        .finally(() => setDetectingCity(false));
    }
  }, [isOpen, city, getPlatformCoords]);

  // Open door sheet when address selected on mobile
  useEffect(() => { if (doorMapSelection) setMobileDoorSheetOpen(true); }, [doorMapSelection]);

  // Error
  useEffect(() => { if (pointsError) setError(pointsError.message); }, [pointsError]);

  // Open pickup sheet when tariffs ready
  useEffect(() => {
    if (!pendingSheetPoint) return;
    const tariff = pickTariffForPoint(pendingSheetPoint.type, tariffs ?? null);
    if (tariffsLoading && !tariff) return;
    setSheetPoint(pendingSheetPoint);
    setMobilePickupSheetOpen(true);
    pickupSheetOpenedAtRef.current = Date.now();
    setPendingSheetPoint(null);
  }, [pendingSheetPoint, tariffsLoading, tariffs]);

  // Fallback: open after 3s if tariff never arrives
  useEffect(() => {
    if (!pendingSheetPoint) return;
    const timer = window.setTimeout(() => {
      setSheetPoint(pendingSheetPoint);
      setMobilePickupSheetOpen(true);
      pickupSheetOpenedAtRef.current = Date.now();
      setPendingSheetPoint(null);
    }, 3000);
    return () => window.clearTimeout(timer);
  }, [pendingSheetPoint]);

  // Prefetch neighboring cells
  useEffect(() => {
    if (!pickupBounds || !isOpen || tab !== 'pickup' || pointsFetching) return;
    const w = pickupBounds.east - pickupBounds.west;
    const h = pickupBounds.north - pickupBounds.south;
    const neighbors: PickupPointBounds[] = [
      { south: pickupBounds.south, north: pickupBounds.north, west: pickupBounds.east,      east: pickupBounds.east + w },
      { south: pickupBounds.south, north: pickupBounds.north, west: pickupBounds.west - w,  east: pickupBounds.west },
      { south: pickupBounds.north, north: pickupBounds.north + h, west: pickupBounds.west,  east: pickupBounds.east },
      { south: pickupBounds.south - h, north: pickupBounds.south, west: pickupBounds.west,  east: pickupBounds.east },
    ];
    const timers = neighbors.map((b, idx) =>
      window.setTimeout(() => {
        const snapped = snapBounds(b);
        void queryClient.prefetchQuery({
          queryKey: ['cdek', 'pickup-points', 'bounds', snapped.south, snapped.west, snapped.north, snapped.east],
          queryFn: ({ signal }) => fetchPickupPointsByBounds(snapped, signal),
          staleTime: POINTS_STALE,
        });
      }, (idx + 1) * 600),
    );
    return () => timers.forEach((t) => window.clearTimeout(t));
  }, [pickupBounds, isOpen, tab, pointsFetching, queryClient]);

  // Handlers
  const handleSelectPoint = useCallback((code: string) => {
    const point = points.find((p) => p.code === code) ?? selectedPointSnapshot;
    if (point?.code === code) {
      setSelectedPointSnapshot(point);
      if (point.city) setCity({ code: point.cityCode, city: point.city, region: point.region || '' });
    }
    setSelectedPointCode(code);
    setError(null);
    setMobileListOpen(false);
    setMobilePickupSheetOpen(false);
    setPendingSheetPoint(point ?? null);
    hapticFeedback?.impactOccurred('light');
  }, [points, selectedPointSnapshot, hapticFeedback]);

  const handleConfirmPickup = useCallback(() => {
    if (!selectedPoint) { setError('Выберите пункт выдачи.'); return; }
    const tariff = pickTariffForPoint(selectedPoint.type, tariffs ?? null);
    setCdekInfo({
      pointCode: selectedPoint.code,
      pointName: selectedPoint.name,
      pointAddress: selectedPoint.fullAddress || selectedPoint.address,
      pointCity: selectedPoint.city,
      pointWorkTime: selectedPoint.workTime,
      cityCode: selectedPoint.cityCode,
      cost: tariff?.deliveryCost ?? 0,
      minDays: tariff?.periodMin ?? 0,
      maxDays: tariff?.periodMax ?? 0,
      tariffCode: tariff?.tariffCode ?? 0,
      latitude: selectedPoint.latitude,
      longitude: selectedPoint.longitude,
      type: selectedPoint.type,
    }, cartFingerprint);
    hapticFeedback?.notificationOccurred('success');
  }, [selectedPoint, tariffs, setCdekInfo, cartFingerprint, hapticFeedback]);

  const handleConfirmDoor = useCallback(() => {
    const address = storeDoorAddress;
    if (!city) { setError('Определите город доставки.'); return; }
    if (!address.street?.trim() || !address.house?.trim()) { setError('Укажите улицу и дом.'); return; }
    if (!doorTariff) { setError('Для выбранного города нет курьерского тарифа СДЭК.'); return; }
    setDoorAddress(address);
    setCdekInfo({
      pointCode: '',
      pointName: 'Курьер СДЭК',
      pointAddress: buildDoorAddressText(city, address),
      pointCity: city.city,
      pointWorkTime: '',
      cityCode: city.code,
      cost: doorTariff.deliveryCost,
      minDays: doorTariff.periodMin,
      maxDays: doorTariff.periodMax,
      tariffCode: doorTariff.tariffCode,
      latitude: doorMapSelection?.latitude ?? 0,
      longitude: doorMapSelection?.longitude ?? 0,
      type: 'DOOR',
    }, cartFingerprint);
    setMobileDoorSheetOpen(false);
    hapticFeedback?.notificationOccurred('success');
  }, [city, storeDoorAddress, doorTariff, doorMapSelection, setCdekInfo, setDoorAddress, cartFingerprint, hapticFeedback]);

  const handleMapCourierPoint = useCallback((coords: [number, number]) => {
    setTab('door');
    setSelectedPointCode(null);
    setError(null);
    setDoorMapSelection({ latitude: coords[0], longitude: coords[1], title: 'Выбранная точка', subtitle: 'Уточняем адрес...' });
    void resolveDoorSelectionFromCoords(coords)
      .then(async ({ selection, address, locationContext }) => {
        const resolvedCity = locationContext ? await resolveCdekCityFromLocationContext(locationContext) : null;
        if (resolvedCity) applyCity(resolvedCity, 'map');
        setDoorAddress({ ...storeDoorAddress, street: address.street ?? '', house: address.house ?? '' });
        setDoorMapSelection(selection);
      })
      .catch(() => setDoorMapSelection({ latitude: coords[0], longitude: coords[1], title: 'Выбранная точка', subtitle: '' }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setDoorAddress, storeDoorAddress]);

  const applyCity = useCallback((nextCity: CdekCity, _source: string) => {
    setCity((current) => current?.code === nextCity.code ? current : nextCity);
  }, []);

  const handlePickupViewportChange = useCallback((bounds: MapBounds, center: [number, number], zoom: number) => {
    setMapBounds(bounds);
    setMapZoom(zoom);
    setMapCenter(center);
  }, []);

  const handlePickupAddressSelect = useCallback((item: DoorSearchResult) => {
    setMapFocus({ coords: [item.latitude, item.longitude], zoom: 14, key: Date.now() });
    setMapCenter([item.latitude, item.longitude]);
    setMapZoom(14);
    setError(null);
  }, []);

  const locateUser = useCallback(async () => {
    setDetectingCity(true);
    try {
      const coords = await getPlatformCoords();
      if (coords) setMapFocus({ coords, zoom: 12, key: Date.now() });
    } finally { setDetectingCity(false); }
  }, [getPlatformCoords]);

  const retry = () => {
    setError(null);
    if (tab === 'pickup') { pickupBounds ? void refetchPoints() : void locateUser(); }
    else { city ? void refetchTariffs() : void locateUser(); }
  };

  if (!isOpen) return null;

  const isInitialPointsLoading = pointsInitialLoading && points.length === 0;
  const isLoading = detectingCity || isInitialPointsLoading;
  const canLoadPickupArea = Boolean(pickupBounds);

  const progressBar = (
    <div className={cn('h-0.5 w-full shrink-0 transition-colors duration-300',
      (pointsFetching || tariffsLoading) && !detectingCity ? 'animate-pulse bg-primary/30' : 'bg-transparent')} />
  );

  const errorBanner = error ? (
    <div className="mx-4 mt-3 shrink-0 rounded-xl border border-red-100 bg-red-50 p-3">
      <div className="flex items-start gap-2">
        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />
        <div className="min-w-0 flex-1">
          <p className="text-[13px] font-medium text-red-700">{error}</p>
          <button type="button" onClick={retry} className="mt-1.5 inline-flex items-center gap-1.5 text-[12px] font-semibold text-red-700">
            <RefreshCw className="h-3.5 w-3.5" /> повторить
          </button>
        </div>
      </div>
    </div>
  ) : null;

  const overlay = (
    <div
      role="dialog" aria-modal="true" aria-label="Выбор доставки СДЭК"
      className="fixed inset-0 z-[200] flex flex-col bg-paper dark:bg-dark-bg text-ink dark:text-white"
    >
      {/* Top bar */}
      <div className="shrink-0 flex items-center gap-2 bg-paper dark:bg-dark-card border-b border-silk/50 dark:border-dark-border px-3 py-2 safe-area-top">
        <div className="flex-1 grid grid-cols-2 gap-1 rounded-xl bg-muted/40 dark:bg-dark-bg p-1">
          {(['pickup', 'door'] as PickerTab[]).map((t) => (
            <button key={t} type="button"
              onClick={() => { setTab(t); setMobileListOpen(false); setMobilePickupSheetOpen(false); hapticFeedback?.selectionChanged(); }}
              className={cn('flex h-10 items-center justify-center gap-2 rounded-lg text-[13px] font-semibold transition-colors',
                tab === t ? 'bg-paper dark:bg-dark-card text-ink dark:text-white shadow-sm' : 'text-muted-foreground active:bg-paper/60')}>
              {t === 'pickup' ? <><Building2 className="h-4 w-4" />Пункт выдачи</> : <><Truck className="h-4 w-4" />Курьером</>}
            </button>
          ))}
        </div>
        <button type="button" onClick={closeWidget} aria-label="Закрыть"
          className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-muted/40 dark:bg-dark-bg text-muted-foreground transition-colors active:bg-muted">
          <X className="h-4 w-4" />
        </button>
      </div>

      {progressBar}
      {errorBanner}

      {/* Map area */}
      <main className="relative flex-1 overflow-hidden bg-muted/20 dark:bg-dark-bg">
        {tab === 'pickup' ? (
          <>
            <AddressSearchBar city={city} placeholder="Введите адрес или место" onSelect={handlePickupAddressSelect} />
            <YandexPickupMap
              city={city} focus={mapFocus} points={visiblePoints} selectedCode={selectedPointCode}
              onSelect={handleSelectPoint} onMapClick={handleMapCourierPoint}
              onViewportChange={handlePickupViewportChange} onReadyChange={setMapReady} onLocate={locateUser}
              initialCenter={mapCenter} initialZoom={mapZoom}
            />
            {/* Списком button */}
            <button type="button" onClick={() => { setMobileListOpen(true); hapticFeedback?.impactOccurred('light'); }}
              className="absolute bottom-0 inset-x-0 z-10 flex items-center justify-center gap-2 border-t border-silk/50 dark:border-dark-border bg-paper dark:bg-dark-card py-3.5">
              <List className="h-4 w-4 text-muted-foreground" />
              <span className="text-[13px] font-semibold text-ink dark:text-white">Списком</span>
            </button>

            {/* Mobile list overlay */}
            {mobileListOpen && (
              <div className="absolute inset-0 z-20 flex flex-col overflow-hidden bg-paper dark:bg-dark-bg">
                <div className="shrink-0 flex items-center justify-between border-b border-silk/50 dark:border-dark-border px-4 py-4">
                  <span className="text-[15px] font-semibold text-ink dark:text-white">
                    {points.length > 0 ? `${points.length} пункт${points.length % 10 === 1 && points.length !== 11 ? '' : points.length % 10 < 5 && (points.length < 10 || points.length > 20) ? 'а' : 'ов'}` : 'Пункты выдачи'}
                  </span>
                  <button type="button" onClick={() => setMobileListOpen(false)} aria-label="Закрыть список"
                    className="grid h-8 w-8 place-items-center rounded-full bg-muted/50 text-ink dark:text-white">
                    <X className="h-4 w-4" />
                  </button>
                </div>
                {progressBar}
                {errorBanner}
                <PickupPointList
                  points={visiblePoints} totalCount={points.length} canLoadArea={canLoadPickupArea}
                  selectedCode={selectedPointCode} loading={isLoading} tariff={selectedPickupTariff}
                  allTariffs={tariffs ?? null}
                  onSelect={(code) => { handleSelectPoint(code); }}
                />
                <div className="shrink-0 border-t border-silk/50 dark:border-dark-border bg-paper dark:bg-dark-card px-4 pt-3 pb-4 safe-area-bottom">
                  <button type="button" onClick={handleConfirmPickup}
                    disabled={!selectedPoint || isLoading || (!!selectedPoint && tariffsLoading && !selectedPickupTariff)}
                    className="flex h-12 w-full items-center justify-center gap-2 rounded-2xl bg-primary text-[14px] font-bold text-white transition-colors active:opacity-90 disabled:cursor-not-allowed disabled:bg-muted disabled:text-muted-foreground">
                    {!selectedPoint ? 'Выберите пункт на карте' : tariffsLoading && !selectedPickupTariff ? 'Рассчитываем стоимость...' : selectedPickupTariff ? (<><Check className="h-4 w-4" />Заберу отсюда · {formatPrice(selectedPickupTariff.deliveryCost)}</>) : (<><Check className="h-4 w-4" />Заберу отсюда</>)}
                  </button>
                </div>
              </div>
            )}

            {/* Pickup confirm sheet */}
            {mobilePickupSheetOpen && sheetPoint && (
              <div className="absolute inset-0 z-20 bg-black/40"
                onPointerDown={(e) => { if (e.target === e.currentTarget) e.preventDefault(); }}
                onClick={(e) => { if (Date.now() - pickupSheetOpenedAtRef.current < 400) return; if (e.target === e.currentTarget) setMobilePickupSheetOpen(false); }}>
                <div className="absolute bottom-0 inset-x-0 flex flex-col rounded-t-[20px] bg-paper dark:bg-dark-card shadow-[0_-8px_40px_rgba(0,0,0,0.2)]">
                  <div className="flex justify-center pt-3 pb-1"><div className="h-1 w-10 rounded-full bg-silk dark:bg-dark-border" /></div>
                  <div className="flex items-center justify-between px-5 py-3 border-b border-silk/50 dark:border-dark-border">
                    <span className="text-[16px] font-bold text-ink dark:text-white">{getPointLabel(sheetPoint)}</span>
                    <button type="button" onClick={() => setMobilePickupSheetOpen(false)}
                      className="grid h-8 w-8 place-items-center rounded-full bg-muted/50 text-ink dark:text-white">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="px-5 pt-4 pb-5 safe-area-bottom">
                    <div className="flex items-start gap-3 mb-4">
                      <div className={cn('grid h-10 w-10 shrink-0 place-items-center rounded-xl',
                        sheetPoint.type === 'POSTAMAT' ? 'bg-primary/10 text-primary' : 'bg-muted/50 text-muted-foreground')}>
                        {sheetPoint.type === 'POSTAMAT' ? <Package className="h-4 w-4" /> : <Building2 className="h-4 w-4" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[15px] font-semibold leading-snug text-ink dark:text-white mb-1">{sheetPoint.fullAddress || sheetPoint.address}</p>
                        {sheetPoint.addressComment && <p className="flex items-center gap-1.5 text-[12px] text-muted-foreground mb-0.5"><MapPin className="h-3.5 w-3.5 shrink-0" />{sheetPoint.addressComment}</p>}
                        {sheetPoint.workTime && <p className="flex items-center gap-1.5 text-[12px] text-muted-foreground"><Clock className="h-3.5 w-3.5 shrink-0" />{getShortWorkTime(sheetPoint.workTime)}</p>}
                      </div>
                    </div>
                    {(() => { const t = pickTariffForPoint(sheetPoint.type, tariffs ?? null); return (t || tariffsLoading) ? (
                      <div className="flex items-center justify-between rounded-xl bg-muted/30 dark:bg-dark-bg px-4 py-3 mb-4">
                        <span className="text-[13px] text-muted-foreground">Стоимость доставки</span>
                        <span className="text-[14px] font-bold text-ink dark:text-white">{tariffsLoading && !t ? 'Считаем...' : t ? `${formatDays(t.periodMin, t.periodMax)} · ${formatPrice(t.deliveryCost)}` : ''}</span>
                      </div>
                    ) : null; })()}
                    <button type="button" onClick={handleConfirmPickup} disabled={isLoading || tariffsLoading}
                      className="flex h-12 w-full items-center justify-center gap-2 rounded-2xl bg-primary text-[14px] font-bold text-white transition-colors active:opacity-90 disabled:bg-muted disabled:text-muted-foreground">
                      {tariffsLoading ? 'Рассчитываем стоимость...' : (() => { const t = pickTariffForPoint(sheetPoint.type, tariffs ?? null); return <><Check className="h-4 w-4" />Заберу отсюда{t ? ` · ${formatPrice(t.deliveryCost)}` : ''}</>; })()}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            <YandexDoorMap city={city} address={storeDoorAddress} selection={doorMapSelection}
              initialCenter={mapCenter} initialZoom={mapZoom}
              onAddressChange={setDoorAddress} onSelectionChange={setDoorMapSelection}
              onCityChange={applyCity} onReadyChange={setMapReady}
              getLocationCoords={getPlatformCoords} />
            {!doorMapSelection && !mobileDoorSheetOpen && (
              <div className="absolute bottom-6 left-1/2 z-10 -translate-x-1/2 rounded-full bg-paper/90 dark:bg-dark-card/90 px-4 py-2 text-[13px] font-semibold text-ink dark:text-white shadow-md backdrop-blur-sm">
                Нажмите на карту для выбора адреса
              </div>
            )}
            {doorMapSelection && !mobileDoorSheetOpen && (
              <button type="button" onClick={() => setMobileDoorSheetOpen(true)}
                className="absolute bottom-0 inset-x-0 z-10 flex items-center gap-3 border-t border-silk/50 dark:border-dark-border bg-paper dark:bg-dark-card px-4 py-3">
                <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-primary text-white"><Navigation className="h-4 w-4" /></span>
                <div className="min-w-0 flex-1 text-left">
                  <p className="truncate text-[13px] font-bold text-ink dark:text-white">{doorMapSelection.title}</p>
                  <p className="truncate text-[11px] text-muted-foreground">Нажмите чтобы уточнить адрес</p>
                </div>
                <span className="text-[12px] font-semibold text-primary">Далее →</span>
              </button>
            )}
            {/* Door bottom sheet */}
            {mobileDoorSheetOpen && (
              <div className="absolute inset-0 z-20 bg-black/20"
                onClick={(e) => { if (e.target === e.currentTarget) setMobileDoorSheetOpen(false); }}>
                <div className="absolute bottom-0 inset-x-0 flex max-h-[82dvh] flex-col rounded-t-[20px] bg-paper dark:bg-dark-card shadow-[0_-8px_40px_rgba(0,0,0,0.2)]">
                  <div className="shrink-0 flex items-center justify-between px-5 py-4 border-b border-silk/50 dark:border-dark-border">
                    <span className="text-[16px] font-bold text-ink dark:text-white">Курьерская доставка</span>
                    <button type="button" onClick={() => setMobileDoorSheetOpen(false)}
                      className="grid h-8 w-8 place-items-center rounded-full bg-muted/50 text-ink dark:text-white">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                  {progressBar}
                  {errorBanner}
                  <DoorPanel city={city} address={storeDoorAddress} selection={doorMapSelection}
                    onAddressChange={setDoorAddress} tariff={doorTariff} loading={tariffsLoading} />
                  <div className="shrink-0 border-t border-silk/50 dark:border-dark-border bg-paper dark:bg-dark-card px-4 pt-3 safe-area-bottom">
                    <button type="button" onClick={handleConfirmDoor}
                      disabled={!city || !doorTariff || tariffsLoading || !storeDoorAddress.street?.trim() || !storeDoorAddress.house?.trim()}
                      className="flex h-12 w-full items-center justify-center gap-2 rounded-2xl bg-primary text-[14px] font-bold text-white transition-colors active:opacity-90 disabled:bg-muted disabled:text-muted-foreground">
                      <Check className="h-4 w-4" />Доставить сюда
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
        {!mapReady && <MapSkeleton />}
      </main>
    </div>
  );

  return createPortal(overlay, document.body);
});

// ============================================================================
// CdekSelectedPoint — display in cdek-delivery-section.tsx
// ============================================================================

export const CdekSelectedPoint = memo(function CdekSelectedPoint({
  deliveryInfo, onChangeClick, className,
}: { deliveryInfo: CdekDeliveryInfo; onChangeClick: () => void; className?: string }) {
  const isDoor = deliveryInfo.type === 'DOOR';
  const isPostamat = deliveryInfo.type === 'POSTAMAT';
  const Icon = isDoor ? Truck : isPostamat ? Package : Building2;
  const iconBg = isDoor ? 'bg-blue-500 text-white' : isPostamat ? 'bg-primary text-white' : 'bg-emerald-500 text-white';
  const typeLabel = isDoor ? 'Курьер' : isPostamat ? 'Постамат' : 'ПВЗ';

  return (
    <div className={cn('p-4 bg-primary/5 dark:bg-primary/10 border border-primary/20 rounded-xl', className)}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className={cn('w-10 h-10 rounded-xl flex items-center justify-center', iconBg)}>
            <Icon className="w-5 h-5" />
          </div>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-[9px] font-bold text-[#00B33C] bg-[#00B33C]/10 px-1.5 py-0.5 rounded">СДЭК</span>
              <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-primary/10 text-primary">{typeLabel}</span>
            </div>
            <p className="text-[14px] font-bold text-ink dark:text-white">
              {deliveryInfo.pointCity || deliveryInfo.pointName}
            </p>
          </div>
        </div>
        <button type="button" onClick={onChangeClick} className="text-[12px] font-bold text-primary">Изменить</button>
      </div>
      <div className="mb-3 pl-[52px]">
        <div className="flex items-start gap-2">
          <MapPin className="w-4 h-4 mt-0.5 shrink-0 text-muted-foreground" />
          <p className="text-[13px] text-ink dark:text-white leading-relaxed">{deliveryInfo.pointAddress}</p>
        </div>
        <div className="flex items-center gap-3 mt-2 text-[11px] text-muted-foreground">
          {!isDoor && deliveryInfo.pointWorkTime && (
            <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{deliveryInfo.pointWorkTime.split(',')[0]}</span>
          )}
          {deliveryInfo.minDays > 0 && (
            <span className="flex items-center gap-1 text-emerald-600 font-medium">
              {deliveryInfo.minDays === deliveryInfo.maxDays ? `${deliveryInfo.minDays} дн.` : `${deliveryInfo.minDays}–${deliveryInfo.maxDays} дн.`}
            </span>
          )}
        </div>
      </div>
      <div className="flex items-center justify-between pt-3 border-t border-primary/10">
        <span className="text-[12px] text-muted-foreground">Примерная стоимость</span>
        <span className="text-[18px] font-bold text-ink dark:text-white">
          {deliveryInfo.cost > 0 ? `~${deliveryInfo.cost} ₽` : 'Бесплатно'}
        </span>
      </div>
    </div>
  );
});
